import { useState, useEffect, useCallback } from 'react';
import {
  FIRESTORE_DB,
  chatEurW
} from '../firebaseConfig';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  startAfter,
  where
} from 'firebase/firestore';

const MESSAGES_BATCH_SIZE = 20;

const DEFAULT_MENTION_USERS = [
  { id: '1', name: 'Alex' },
  { id: '2', name: 'Jordan' },
  { id: '3', name: 'Sam' },
  { id: '4', name: 'Taylor' },
  { id: '5', name: 'Casey' }
];

/**
 * Reusable F8 chat logic hook.
 * @param {Object} options
 * @param {string|null} options.userId - Firebase user ID, null if not authenticated
 * @param {boolean} options.isAnonymous - If true, skip Firestore and keep messages in local state only
 */
export default function useF8Chat({ userId, isAnonymous = false }) {
  // Thread management state
  const [threads, setThreads] = useState([]);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [chatMode, setChatMode] = useState('initial'); // 'initial' or 'thread'

  // Chat state
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionUsers, setMentionUsers] = useState([]);
  const [filteredMentionUsers, setFilteredMentionUsers] = useState([]);

  // Pagination state
  const [oldestMessageDocForPagination, setOldestMessageDocForPagination] = useState(null);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [hasMoreOlderMessages, setHasMoreOlderMessages] = useState(true);
  const [newestMessageTimestampLoaded, setNewestMessageTimestampLoaded] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Format timestamp helper
  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = (now - date) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return date.toLocaleDateString();
  }, []);

  // Fetch people for mentions
  useEffect(() => {
    if (isAnonymous || !userId) {
      setMentionUsers(DEFAULT_MENTION_USERS);
      return;
    }

    const fetchPeople = async () => {
      try {
        const peopleRef = collection(FIRESTORE_DB, `users/${userId}/people`);
        const peopleSnap = await getDocs(peopleRef);

        const peopleData = [];
        peopleSnap.forEach(doc => {
          const person = doc.data();
          if (person.firstName) {
            peopleData.push({
              id: doc.id,
              name: person.firstName
            });
          }
        });

        setMentionUsers(peopleData.length > 0 ? peopleData : DEFAULT_MENTION_USERS);
      } catch (error) {
        console.error("Error fetching people for mentions:", error);
        setMentionUsers(DEFAULT_MENTION_USERS);
      }
    };

    fetchPeople();
  }, [userId, isAnonymous]);

  // Load threads from Firestore
  const loadThreads = useCallback(async () => {
    if (isAnonymous || !userId) return;

    try {
      const threadsRef = collection(FIRESTORE_DB, `users/${userId}/chatThreads`);
      const threadsQuery = query(threadsRef, orderBy("lastMessageAt", "desc"), limit(30));
      const snapshot = await getDocs(threadsQuery);

      const loadedThreads = [];
      snapshot.forEach((doc) => {
        loadedThreads.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setThreads(loadedThreads);

      // Only reset to 'initial' on first load when no thread is active.
      // If a thread is already being loaded (e.g. via deep-link), don't override.
      if (loadedThreads.length > 0) {
        setCurrentThreadId((current) => {
          if (!current) setChatMode('initial');
          return current;
        });
      }
    } catch (error) {
      console.error("Error loading threads:", error);
    }
  }, [userId, isAnonymous]);

  // Create a new thread
  const createNewThread = useCallback(async (firstMessage = null) => {
    if (isAnonymous || !userId) return null;

    try {
      const threadsRef = collection(FIRESTORE_DB, `users/${userId}/chatThreads`);
      const newThread = {
        title: firstMessage ? firstMessage.substring(0, 25) + "..." : "New Chat",
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        messageCount: 0
      };

      const threadDoc = await addDoc(threadsRef, newThread);
      const threadId = threadDoc.id;

      const threadWithId = { ...newThread, id: threadId };
      setThreads(prev => [threadWithId, ...prev]);
      setCurrentThreadId(threadId);
      setChatMode('thread');

      setMessages([]);
      setOldestMessageDocForPagination(null);
      setHasMoreOlderMessages(true);
      setNewestMessageTimestampLoaded(null);
      setInitialLoadComplete(false);

      return threadId;
    } catch (error) {
      console.error("Error creating thread:", error);
      return null;
    }
  }, [userId, isAnonymous]);

  // Load messages for a specific thread
  const loadThreadMessages = useCallback(async (threadId) => {
    if (isAnonymous || !userId || !threadId) return;

    setIsLoading(true);
    setMessages([]);
    setCurrentThreadId(threadId);
    setChatMode('thread');

    try {
      const messagesRef = collection(FIRESTORE_DB, `users/${userId}/chatThreads/${threadId}/messages`);
      const messagesQuery = query(
        messagesRef,
        orderBy("timestamp", "desc"),
        limit(MESSAGES_BATCH_SIZE)
      );

      const snapshot = await getDocs(messagesQuery);
      const loadedMessages = [];
      snapshot.forEach((doc) => {
        loadedMessages.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      const reversedMessages = loadedMessages.reverse();
      setMessages(reversedMessages);

      if (snapshot.docs.length > 0) {
        setOldestMessageDocForPagination(snapshot.docs[snapshot.docs.length - 1]);
        setNewestMessageTimestampLoaded(reversedMessages[reversedMessages.length - 1]?.timestamp);
      }
      setHasMoreOlderMessages(snapshot.docs.length === MESSAGES_BATCH_SIZE);
      setInitialLoadComplete(true);

    } catch (error) {
      console.error("Error loading thread messages:", error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, isAnonymous]);

  // Delete thread
  const deleteThread = useCallback(async (threadId) => {
    if (isAnonymous || !userId || !threadId) return;

    try {
      const messagesRef = collection(FIRESTORE_DB, `users/${userId}/chatThreads/${threadId}/messages`);
      const messagesSnapshot = await getDocs(messagesRef);

      const deletePromises = messagesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      const threadRef = doc(FIRESTORE_DB, `users/${userId}/chatThreads/${threadId}`);
      await deleteDoc(threadRef);

      setThreads(prev => prev.filter(thread => thread.id !== threadId));

      if (currentThreadId === threadId) {
        setCurrentThreadId(null);
        setMessages([]);
        setChatMode('initial');
      }

    } catch (error) {
      console.error("Error deleting thread:", error);
    }
  }, [userId, isAnonymous, currentThreadId]);

  // Load threads on mount
  useEffect(() => {
    if (!isAnonymous && userId) {
      loadThreads();
    }
  }, [userId, isAnonymous, loadThreads]);

  // Real-time message listener
  useEffect(() => {
    if (isAnonymous || !userId || !currentThreadId || !initialLoadComplete) {
      return () => {};
    }

    const messagesRef = collection(FIRESTORE_DB, `users/${userId}/chatThreads/${currentThreadId}/messages`);
    let newMessagesQuery;

    if (newestMessageTimestampLoaded) {
      newMessagesQuery = query(
        messagesRef,
        orderBy("timestamp", "asc"),
        where("timestamp", ">", newestMessageTimestampLoaded)
      );
    } else {
      newMessagesQuery = query(messagesRef, orderBy("timestamp", "asc"));
    }

    const unsubscribeNewMessages = onSnapshot(newMessagesQuery, (snapshot) => {
      let latestTimestamp = newestMessageTimestampLoaded;

      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          const newMessage = { id: change.doc.id, ...change.doc.data() };

          if (newMessage.timestamp && (!latestTimestamp || newMessage.timestamp > latestTimestamp)) {
            latestTimestamp = newMessage.timestamp;
          }

          setMessages((prevMessages) => {
            if (!prevMessages.find(msg => msg.id === newMessage.id)) {
              return [...prevMessages, newMessage];
            }
            return prevMessages;
          });
        }
      });

      if (latestTimestamp && latestTimestamp !== newestMessageTimestampLoaded) {
        setNewestMessageTimestampLoaded(latestTimestamp);
      }
    }, (error) => {
      console.error("Error in new messages snapshot listener:", error);
    });

    return () => {
      unsubscribeNewMessages();
    };
  }, [userId, isAnonymous, currentThreadId, initialLoadComplete, newestMessageTimestampLoaded]);

  // Load older messages (pagination)
  const loadOlderMessages = useCallback(async () => {
    if (isAnonymous || !userId || !currentThreadId || isLoadingOlderMessages || !hasMoreOlderMessages || !oldestMessageDocForPagination) {
      return;
    }

    setIsLoadingOlderMessages(true);
    try {
      const messagesRef = collection(FIRESTORE_DB, `users/${userId}/chatThreads/${currentThreadId}/messages`);
      const olderMessagesQuery = query(
        messagesRef,
        orderBy("timestamp", "desc"),
        startAfter(oldestMessageDocForPagination),
        limit(MESSAGES_BATCH_SIZE)
      );

      const snapshot = await getDocs(olderMessagesQuery);
      const loadedOlderMessages = [];
      snapshot.forEach((doc) => {
        loadedOlderMessages.push({ id: doc.id, ...doc.data() });
      });

      if (loadedOlderMessages.length > 0) {
        setMessages((prevMessages) => [...loadedOlderMessages.reverse(), ...prevMessages]);
        setOldestMessageDocForPagination(snapshot.docs[snapshot.docs.length - 1]);
      }
      setHasMoreOlderMessages(loadedOlderMessages.length === MESSAGES_BATCH_SIZE);

    } catch (error) {
      console.error("Error loading older messages:", error);
    } finally {
      setIsLoadingOlderMessages(false);
    }
  }, [isAnonymous, userId, currentThreadId, isLoadingOlderMessages, hasMoreOlderMessages, oldestMessageDocForPagination]);

  // Parse AI response into message + suggested responses + cards
  const parseAIResponse = useCallback((response) => {
    let botMessage = "I'm here to help! 💫";
    let suggestedResponses = [];
    let cards = null;

    try {
      const data = response?.data;
      if (!data) return { botMessage, suggestedResponses, cards };

      if (Array.isArray(data.cards) && data.cards.length > 0) {
        cards = data.cards;
      }

      if (data.message) {
        let msg = data.message;

        // If the message contains ```json, extract and parse it
        if (typeof msg === 'string' && msg.includes('```')) {
          const cleaned = msg.replace(/```(json|javascript|js)?|```/g, '').trim();
          try {
            const jsonMatch = cleaned.match(/({[\s\S]*})/);
            const parsed = JSON.parse(jsonMatch ? jsonMatch[1] : cleaned);
            botMessage = parsed.message || cleaned;
            suggestedResponses = parsed.responses || suggestedResponses;
            return { botMessage, suggestedResponses, cards };
          } catch {
            // Not valid JSON inside code block, use as plain text
            botMessage = cleaned;
          }
        } else if (typeof msg === 'string') {
          // Try parsing as JSON first (might be a JSON string without code blocks)
          try {
            const parsed = JSON.parse(msg);
            botMessage = parsed.message || msg;
            suggestedResponses = parsed.responses || suggestedResponses;
          } catch {
            // Plain text message
            botMessage = msg;
          }
        } else if (typeof msg === 'object') {
          botMessage = msg.message || botMessage;
          suggestedResponses = msg.responses || suggestedResponses;
        }

        // Also check top-level suggested_responses from the cloud function
        if (data.suggested_responses && data.suggested_responses.length > 0) {
          suggestedResponses = data.suggested_responses;
        }
      }
    } catch (parseError) {
      console.error("Error parsing AI response:", parseError);
      botMessage = "I understand what you're sharing. Can you tell me more about what's on your mind?";
    }

    return { botMessage, suggestedResponses, cards };
  }, []);

  // Send message
  const sendMessage = useCallback(async (text) => {
    if (!text || !text.trim() || isLoading) return;

    const trimmedText = text.trim();

    // Anonymous mode: local-only messages, still call chatEurW
    if (isAnonymous) {
      setChatMode('thread');
      const userMsg = {
        id: `anon-user-${Date.now()}`,
        text: trimmedText,
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMsg]);
      setIsLoading(true);

      try {
        const response = await chatEurW({
          message: trimmedText,
          mentions: [],
          threadId: null,
          platform: 'web',
        });

        // Check for rate limiting
        if (response?.data?.error === 'rate_limited') {
          const rateLimitMsg = {
            id: `anon-ratelimit-${Date.now()}`,
            text: response.data.message || "You've reached the chat limit. Sign in for unlimited access.",
            sender: 'bot',
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, rateLimitMsg]);
          setIsLoading(false);
          return;
        }

        const { botMessage, suggestedResponses, cards } = parseAIResponse(response);

        const botMsg = {
          id: `anon-bot-${Date.now()}`,
          text: botMessage,
          sender: 'bot',
          timestamp: new Date(),
          suggested_responses: suggestedResponses,
          ...(cards ? { cards } : {}),
        };
        setMessages(prev => [...prev, botMsg]);
      } catch (error) {
        console.error("[F8] Error sending anonymous message:", error, error?.code, error?.message);
        const errorMsg = {
          id: `anon-error-${Date.now()}`,
          text: "Sorry, I'm having trouble connecting right now. Please try again later.",
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMsg]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Authenticated mode: full Firestore persistence
    if (!userId) return;

    let threadId = currentThreadId;
    if (!threadId) {
      threadId = await createNewThread(trimmedText);
      if (!threadId) return;
    }

    setIsLoading(true);

    try {
      const messagesRef = collection(FIRESTORE_DB, `users/${userId}/chatThreads/${threadId}/messages`);
      await addDoc(messagesRef, {
        text: trimmedText,
        sender: "user",
        timestamp: serverTimestamp(),
      });

      // Extract mentions
      const mentionRegex = /@(\w+)/g;
      const mentions = [];
      let match;
      while ((match = mentionRegex.exec(trimmedText)) !== null) {
        const mentionName = match[1];
        const person = mentionUsers.find(u => u.name.toLowerCase() === mentionName.toLowerCase());
        if (person) {
          mentions.push({ name: mentionName, id: person.id });
        }
      }

      const response = await chatEurW({
        message: trimmedText,
        mentions: mentions,
        threadId: threadId,
        platform: 'web',
      });

      const { botMessage, suggestedResponses, cards } = parseAIResponse(response);

      await addDoc(messagesRef, {
        text: botMessage,
        sender: "bot",
        timestamp: serverTimestamp(),
        suggested_responses: suggestedResponses,
        ...(cards ? { cards } : {}),
      });

      // Update thread metadata
      try {
        const threadRef = doc(FIRESTORE_DB, `users/${userId}/chatThreads/${threadId}`);
        const updateData = {
          lastMessageAt: serverTimestamp(),
          messageCount: messages.length + 2,
        };
        if (messages.length === 0) {
          updateData.title = trimmedText.substring(0, 25) + "...";
        }
        await setDoc(threadRef, updateData, { merge: true });
      } catch (firestoreError) {
        console.error("Error updating thread metadata:", firestoreError);
      }

      // For new threads, initialize the real-time listener
      const needsInitialization = !initialLoadComplete || messages.length === 0;
      if (needsInitialization) {
        try {
          const initMessagesQuery = query(
            messagesRef,
            orderBy("timestamp", "desc"),
            limit(10)
          );

          const initSnapshot = await getDocs(initMessagesQuery);
          const initMessages = [];
          initSnapshot.forEach((doc) => {
            initMessages.push({ id: doc.id, ...doc.data() });
          });

          const reversedInitMessages = initMessages.reverse();
          setMessages(reversedInitMessages);

          if (reversedInitMessages.length > 0) {
            setNewestMessageTimestampLoaded(reversedInitMessages[reversedInitMessages.length - 1].timestamp);
          }

          setInitialLoadComplete(true);
        } catch (initError) {
          console.error("Error initializing chat:", initError);
          setInitialLoadComplete(true);
        }
      }

      // Update threads list
      setThreads(prev => prev.map(thread =>
        thread.id === threadId
          ? {
              ...thread,
              lastMessage: trimmedText,
              timestamp: new Date(),
              messageCount: (thread.messageCount || 0) + 2
            }
          : thread
      ));

    } catch (error) {
      console.error("Error sending message:", error);

      if (threadId) {
        try {
          const messagesRef = collection(FIRESTORE_DB, `users/${userId}/chatThreads/${threadId}/messages`);
          await addDoc(messagesRef, {
            text: "Sorry, I'm having trouble connecting right now. Please try again later.",
            sender: "bot",
            timestamp: serverTimestamp(),
          });
        } catch (errorMessageError) {
          console.error("Failed to add error message:", errorMessageError);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, isAnonymous, userId, currentThreadId, createNewThread, mentionUsers, messages.length, initialLoadComplete, parseAIResponse]);

  // Reset chat state
  const resetChat = useCallback(() => {
    setMessages([]);
    setChatMode('initial');
    setCurrentThreadId(null);
    setInitialLoadComplete(false);
    setOldestMessageDocForPagination(null);
    setHasMoreOlderMessages(true);
    setNewestMessageTimestampLoaded(null);
  }, []);

  // Detect @mentions in input text
  const handleMentionDetection = useCallback((text, cursorPos) => {
    const textBeforeCursor = text.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const q = mentionMatch[1];
      setShowMentions(true);
      const filtered = mentionUsers.filter(user =>
        user.name.toLowerCase().includes(q.toLowerCase())
      );
      setFilteredMentionUsers(filtered);
    } else {
      setShowMentions(false);
    }
  }, [mentionUsers]);

  // Handle mention selection — returns new text and cursor position
  const handleMentionSelect = useCallback((user, inputText, cursorPos) => {
    const textBeforeCursor = inputText.substring(0, cursorPos);
    const textAfterCursor = inputText.substring(cursorPos);

    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      const beforeMention = textBeforeCursor.substring(0, mentionMatch.index);
      const newText = beforeMention + `@${user.name} ` + textAfterCursor;
      const newCursorPos = beforeMention.length + user.name.length + 2;
      setShowMentions(false);
      return { newText, newCursorPos };
    }

    setShowMentions(false);
    return { newText: inputText, newCursorPos: cursorPos };
  }, []);

  // Render text with @mention highlighting
  const renderTextWithMentions = useCallback((text, className, mentionClassName) => {
    if (!text) return null;

    const mentionRegex = /@(\w+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: text.substring(lastIndex, match.index), isMention: false });
      }
      parts.push({ text: match[0], isMention: true });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.substring(lastIndex), isMention: false });
    }

    if (parts.length === 0) {
      return <span className={className}>{text}</span>;
    }

    return (
      <span className={className}>
        {parts.map((part, index) => (
          <span key={index} className={part.isMention ? mentionClassName : ''}>
            {part.text}
          </span>
        ))}
      </span>
    );
  }, []);

  return {
    // State
    threads,
    currentThreadId,
    chatMode,
    messages,
    isLoading,
    mentionUsers,
    filteredMentionUsers,
    showMentions,
    hasMoreOlderMessages,
    isLoadingOlderMessages,
    // Actions
    loadThreads,
    createNewThread,
    loadThreadMessages,
    deleteThread,
    sendMessage,
    loadOlderMessages,
    resetChat,
    handleMentionDetection,
    handleMentionSelect,
    // Helpers
    renderTextWithMentions,
    formatTimestamp,
    // Setters
    setShowMentions,
  };
}
